import { useState } from 'react';
import { Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { CameraScanner, isScanButtonAvailable } from '~/components/camera-scanner';
import { BackLink } from '~/components/back-link/back-link';
import { BoletoIcon } from '~/components/icons/boleto-icon';
import { validateBillingOrigin } from '~/api';
import { onlyDigits } from '~/helpers/validation/normalize';
import { colors } from '~/theme';
import { LoadingScreen } from '~/screens/loading/loading';
import { ResultScreen } from '~/screens/result/result';
import { resolveBoletoAction } from './validate-boleto.logic';

type ScreenState = { step: 'form' | 'scanning' | 'loading' } | { step: 'error' } | { step: 'result'; isValid: boolean };

export const ValidateBoletoScreen = () => {
  const navigate = useNavigate();
  const [rawInput, setRawInput] = useState('');
  const [state, setState] = useState<ScreenState>({ step: 'form' });

  const submit = async (value: string) => {
    const action = resolveBoletoAction(value);

    if (action.type === 'invalid') {
      setState({ step: 'error' });
      return;
    }

    if (action.type === 'reject') {
      setState({ step: 'result', isValid: false });
      return;
    }

    setState({ step: 'loading' });
    const { isValid } = await validateBillingOrigin(action.request);
    setState({ step: 'result', isValid });
  };

  if (state.step === 'loading') {
    return <LoadingScreen />;
  }

  if (state.step === 'result') {
    return (
      <ResultScreen
        variant={state.isValid ? 'success' : 'failure'}
        subject="boleto"
        onNewValidation={() => {
          setRawInput('');
          setState({ step: 'form' });
          navigate('/onboarding');
        }}
      />
    );
  }

  if (state.step === 'scanning') {
    return (
      <Box height="70vh">
        <CameraScanner
          formats={['ITF']}
          onDetect={(value) => {
            setState({ step: 'form' });
            setRawInput(value);
            submit(value);
          }}
          onClose={() => setState({ step: 'form' })}
        />
      </Box>
    );
  }

  return (
    <Stack spacing={3} px={3} py={4}>
      <BackLink />

      <Divider sx={{ borderColor: colors.gray }} />

      <Stack spacing={1}>
        <Typography variant="h1" fontWeight={700} sx={{ fontSize: '16px', color: colors.charcoal }}>
          VALIDAR BOLETO
        </Typography>
        <Typography variant="body2" sx={{ color: colors.darkGray }}>
          Cole a linha digitável do boleto no campo abaixo ou escaneie o Código de barras
        </Typography>
      </Stack>

      {isScanButtonAvailable() && (
        <Button
          variant="outlined"
          size="large"
          fullWidth
          startIcon={<BoletoIcon />}
          onClick={() => setState({ step: 'scanning' })}
        >
          ESCANEAR
        </Button>
      )}

      <TextField
        multiline
        minRows={3}
        label="Linha digitável"
        placeholder="Cole a linha digitável do boleto"
        value={rawInput}
        onChange={(event) => setRawInput(onlyDigits(event.target.value))}
      />

      {state.step === 'error' && (
        <Typography variant="body2" color="error">
          Código inválido. Confira se colou o código completo e tente novamente.
        </Typography>
      )}

      <Button variant="contained" size="large" fullWidth onClick={() => submit(rawInput)} disabled={!rawInput}>
        VALIDAR
      </Button>

      <Divider sx={{ borderColor: colors.gray }} />

      <Stack spacing={1}>
        <Typography variant="body2" fontWeight={700} sx={{ color: colors.neutralBluish600 }}>
          Exemplo:
        </Typography>
        <Box
          component="img"
          src="/boleto/boleto-exemplo.png"
          alt="Exemplo de boleto com a linha digitável e o código de barras destacados"
          sx={{ width: '299px', maxWidth: '100%', borderRadius: '3px', boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.1)' }}
        />
      </Stack>
    </Stack>
  );
};
